/**
 * Inbox status colors and line icons (owner's scheme, #dca-dev 2026-10-22).
 * The workers pick the stage (inbox-stage.ts there); this file only says how
 * each one looks and reads.
 */
import {
  BadgeDollarSign,
  CalendarCheck,
  CalendarClock,
  CircleAlert,
  Flame,
  Link2,
  Repeat,
  Snowflake,
  ThumbsDown,
  ThumbsUp,
  type LucideIcon,
} from 'lucide-react'
import type { InboxSignal, InboxStage } from './use-inbox'

/**
 * Contrast (owner, 2026-10-24): the dot shows little color, so Replied uses a
 * lighter blue that stands apart from purple, and hot / interested also get an
 * icon so they never rely on color alone.
 */
export const STAGE_STYLES: Record<InboxStage, { dot: string; label: string; icon?: { Icon: LucideIcon; className: string } }> = {
  attention: { dot: 'bg-purple-500', label: 'Needs you' },
  booked: { dot: 'bg-green-500', label: 'Booked or sale' },
  not_interested: { dot: 'bg-red-500', label: 'Not interested' },
  hot: {
    dot: 'bg-orange-500',
    label: 'Hot: interested, meeting proposed or link sent',
    icon: { Icon: Flame, className: 'text-orange-400' },
  },
  interested: { dot: 'bg-yellow-400', label: 'Interested', icon: { Icon: ThumbsUp, className: 'text-yellow-400' } },
  replied: { dot: 'bg-blue-400', label: 'Replied' },
  no_reply: { dot: 'bg-sky-300', label: 'No reply yet' },
}

/** Listed in the order the icons appear. */
export const SIGNAL_ICONS: Record<InboxSignal, { Icon: LucideIcon; label: string; className: string }> = {
  attention: { Icon: CircleAlert, label: 'Needs you', className: 'text-purple-400' },
  sale: { Icon: BadgeDollarSign, label: 'Sale', className: 'text-green-400' },
  booked: { Icon: CalendarCheck, label: 'Meeting booked', className: 'text-green-400' },
  meeting_proposed: { Icon: CalendarClock, label: 'Meeting proposed', className: 'text-orange-400' },
  link_sent: { Icon: Link2, label: 'Link sent', className: 'text-muted-foreground' },
  follow_up: { Icon: Repeat, label: 'In follow-ups', className: 'text-muted-foreground' },
  cold: { Icon: Snowflake, label: 'Cold: every step sent, no reply', className: 'text-sky-300' },
  not_interested: { Icon: ThumbsDown, label: 'Not interested', className: 'text-red-400' },
}
